import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task, type TaskInstance } from 'ember-concurrency';
import type { ParityInsightHistoricalData } from 'lumen/models';
import type { BreakdownItemsPerExtractDate } from 'lumen/models/parity/insight/historical-data';
import type { RouteQueryParams as MarketParityInsightParams } from 'lumen/routes/market/parity/insight';
import type { Transition } from 'lumen/services/transition';
import {
  APIGATEWAY,
  type MealTypeValues,
  type MembershipType,
  MEMBERSHIPTYPES,
} from 'lumen/utils/constants/general-constants';
import {
  type Breakdown,
  type CountryCodeWithDefault,
  type DateAggregation,
  HISTORICAL_POINTS_AMOUNT,
  type ParityFilter,
  type RateType,
} from 'lumen/utils/constants/pi-constant-values';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export type TrendsDataRequestParams = {
  'aggregations[]': Breakdown;
  base_membership_type?: MembershipType;
  date_aggregation: DateAggregation;
  extract_date: string;
  filters?: string;
  los: number;
  market_id?: string | null;
  mealtype: MealTypeValues;
  periods: number;
  pos: CountryCodeWithDefault;
  rate_type: RateType;
  subscription_id?: string | null;
};

export type RouteQueryParams = {
  segmentation: Breakdown;
  selection: ParityFilter;
  trendsExtractDate: string;
};

export default class MarketParityInsightTrendsRoute extends Route {
  queryParams = {
    segmentation: {
      refreshModel: true,
    },
    selection: {
      refreshModel: false,
    },
    trendsExtractDate: {
      refreshModel: true,
    },
  };

  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  fetchTrendsDataTask = task(async (params: RouteQueryParams) => {
    try {
      const {
        dateAggregation,
        extractDate,
        segmentationFilters,
        los,
        mealPlan,
        pos,
        rateType,
        showMemberRate,
      } = this.paramsFor('market.parity.insight') as MarketParityInsightParams;

      const requestParams: TrendsDataRequestParams = {
        'aggregations[]': params.segmentation,
        date_aggregation: dateAggregation,
        extract_date: params.trendsExtractDate ?? extractDate,
        market_id: this.hotelAndMarketService.hotelOrMarket.id,
        periods: HISTORICAL_POINTS_AMOUNT,
        los,
        mealtype: mealPlan,
        pos,
        rate_type: rateType,
      };

      if (segmentationFilters.length) {
        requestParams.filters = segmentationFilters;
      }

      if (showMemberRate) {
        requestParams.base_membership_type = MEMBERSHIPTYPES.MEMBER_ONLY;
      }

      const trendsData =
        (await this.store.queryRecord<ParityInsightHistoricalData>(
          'parity/insight/historical-data',
          {
            namespace: APIGATEWAY,
            typeKey: 'parityanalytics/api',
            ...requestParams,
          },
        ))!;

      return trendsData.getBreakdownData(params.segmentation);
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  beforeModel(transition: Transition): void {
    this.transition.validateParityInsightTrendsQueryParams(transition);
  }

  model(params: RouteQueryParams): {
    trendsData: TaskInstance<BreakdownItemsPerExtractDate>;
  } {
    return {
      trendsData: this.fetchTrendsDataTask.perform(params),
    };
  }
}
